import { BlockTitle } from "@blocks/Blocks";
import Container from "@components/Container/Container";

/**
 * This is the layout for the Help page.
 */
const HelpLayout = (): React.ReactElement => {
  return (
    <Container
      rootClassName="!mt-6 px-3 font-poppins"
      data-testid="help-layout-component"
    >
      <div>
        <BlockTitle
          text="Categories: "
          rootClassName="mb-4 text-5xl font-medium lg:text-6xl"
        />

        <div>
          <ul className="mb-4 list-decimal pl-8">
            <li>
              <a href="#">Device and Browser Support</a>
            </li>

            <li>
              <a href="#subscription-information">Subscription Information</a>
            </li>

            <li>
              <a href="#login-and-account-management">
                Login and Account Management
              </a>
            </li>

            <li>
              <a href="#website-and-mobile-app-features">
                Website/Mobile App Features
              </a>
            </li>

            <li>
              <a href="#general-inquiries">General Inquiries</a>
            </li>

            <li>
              <a href="#technical-issues">Technical Issues</a>
            </li>
          </ul>
        </div>
      </div>

      <div id="device-and-browser-support">
        <BlockTitle
          text="1. Device and Browser Support"
          rootClassName="mb-4 text-verticals-btblue text-5xl font-medium lg:text-6xl"
        />

        <div>
          <p>
            <strong>
              What devices and browsers are supported for accessing
              businesstimes.com.sg?
            </strong>
          </p>

          <ul className="mb-4 list-disc pl-16">
            <li>
              You can access the website on any device: desktop, laptop, tablet,
              or smartphone.
            </li>

            <li>
              For the best experience, use updated versions of popular browsers
              like Safari, Chrome, or Firefox.
            </li>
          </ul>
        </div>

        <div>
          <p>
            <strong>
              Is The Business Times website available on mobile and tablet
              devices?
            </strong>
          </p>

          <ul className="mb-4 list-disc pl-16">
            <li>
              Yes, the website works on all devices and adjusts to fit your
              screen.
            </li>

            <li>
              Mobile apps are also available for iOS (App Store) and Android
              (Google Play Store).
            </li>

            <li>
              On App we support the current version of OS + 2 older versions.
            </li>
          </ul>
        </div>
      </div>

      <div id="subscription-information">
        <BlockTitle
          text="2. Subscription Information"
          rootClassName="mb-4 text-verticals-btblue text-5xl font-medium lg:text-6xl"
        />

        <div className="mb-8">
          <p>
            <strong>What benefits do I get by subscribing to BT?</strong>
          </p>

          <p>
            Subscribers get full access to premium content, including in-depth
            features, opinion pieces, and interactive graphics. Premium content
            is marked with a &quot;Subscriber&quot; label.
          </p>
        </div>

        <div>
          <p>
            <strong>How do I subscribe to The Business Times?</strong>
          </p>

          <ul className="mb-4 list-disc pl-16">
            <li>
              Subscribe
              <a
                className="mx-1 text-verticals-btblue underline hover:text-black"
                target="_blank"
                href="https://subscribe.sph.com.sg/publications-bt/"
                rel="noreferrer"
              >
                online
              </a>
              or call (65) 6388 3838.
            </li>

            <li>
              Print newspaper subscribers can upgrade to include website and app
              access.
            </li>

            <li>
              Details of various BT&apos;s subscription plans can be found
              <a
                className="ml-1 text-verticals-btblue underline hover:text-black"
                target="_blank"
                href="https://subscribe.sph.com.sg/publications-bt/"
                rel="noreferrer"
              >
                here
              </a>
              .
            </li>
          </ul>
        </div>

        <div>
          <p>
            <strong>
              How do I add website/App subscriptions to my print subscription?
            </strong>
          </p>

          <ul className="mb-4 list-disc pl-16">
            <li>
              Subscribe
              <a
                className="mx-1 text-verticals-btblue underline hover:text-black"
                target="_blank"
                href="https://subscribe.sph.com.sg/publications-bt/"
                rel="noreferrer"
              >
                online
              </a>
              or call (65) 6388 3838.
            </li>
            <li>
              Please email us at
              <a
                className="mx-1 text-verticals-btblue underline hover:text-black"
                target="_blank"
                href="mailTo:btocs@sph.com.sg"
                rel="noreferrer"
              >
                btocs@sph.com.sg
              </a>
              or contact our customer service team at (65) 6388 3838, or. The
              phone line is manned from 8am-6pm on
            </li>
            <li>
              Details of various BT&apos;s subscription plans can be found
              <a
                className="ml-1 text-verticals-btblue underline hover:text-black"
                target="_blank"
                href="https://subscribe.sph.com.sg/publications-bt/"
                rel="noreferrer"
              >
                here
              </a>
              .
            </li>
          </ul>
        </div>

        <div className="mb-8">
          <p>
            <strong>Can I share articles from the mobile apps?</strong>
          </p>
          <p className="pl-8">
            Yes, use the &quot;Share&quot; button at the top of each article to
            share the link.
          </p>
        </div>

        <div>
          <p>
            <strong>How to suspend print delivery?</strong>
          </p>

          <ul className="mb-4 list-disc pl-16">
            <li>
              Subscribe
              <a
                className="mx-1 text-verticals-btblue underline hover:text-black"
                target="_blank"
                href="https://subscribe.sph.com.sg/publications-bt/"
                rel="noreferrer"
              >
                online
              </a>
              or call (65) 6388 3838.
            </li>
            <li>
              Go to your
              <a
                className="mx-1 text-verticals-btblue underline hover:text-black"
                target="_blank"
                href="https://subscribe.sph.com.sg/your-account/"
                rel="noreferrer"
              >
                account management page
              </a>
              and click &quot;Suspend Print Delivery&quot; to submit a request.
              For more details, visit our
              <a
                className="ml-1 text-verticals-btblue underline"
                target="_blank"
                href="https://consumerfaq.sph.com.sg/kb?id=kb_article_view&sys_kb_id=8fc4ee36977cde10eeee7301f053afe1"
                rel="noreferrer"
              >
                consumer page
              </a>
              .
            </li>
          </ul>
        </div>
      </div>

      <div id="login-and-account-management">
        <BlockTitle
          text="3. Login and Account Management"
          rootClassName="mb-4 text-verticals-btblue text-5xl font-medium lg:text-6xl"
        />

        <div>
          <p>
            <strong>I forgot my password. How do I reset it?</strong>
          </p>

          <ul className="mb-4 list-disc pl-16">
            <li>
              For username issues please email customer service team at
              <a
                className="mx-1 text-verticals-btblue underline hover:text-black"
                target="_blank"
                href="mailTo:btocs@sph.com.sg"
                rel="noreferrer"
              >
                btocs@sph.com.sg
              </a>
              or call (65) 6388 3838
            </li>

            <li>
              For password reset, click &quot;Forgot Password&quot; on the login
              page.
            </li>
          </ul>
        </div>

        <div>
          <p>
            <strong>How do I change my password?</strong>
          </p>

          <ul className="mb-4 list-disc pl-16">
            <li>
              Password can be updated from the
              <a
                className="mx-1 text-verticals-btblue underline hover:text-black"
                target="_blank"
                href="https://www.businesstimes.com.sg/my-account"
                rel="noreferrer"
              >
                &apos;My-Account&apos;
              </a>
              page
            </li>

            <li>
              Click <strong>&quot;Update Your Password&quot;</strong> in the
              left panel
            </li>

            <li>
              The update password pop-up will be shown once clicking on
              <strong>&quot;Update your Password&quot;</strong>.<br />
              You can enter your Old and New Passwords here.
            </li>
          </ul>
        </div>

        <div className="mb-8">
          <p>
            <strong>How do I change my email address or username?</strong>
          </p>

          <p>
            To change the email address or username, please contact our customer
            service team at
            <a
              className="mx-1 text-verticals-btblue underline hover:text-black"
              target="_blank"
              href="mailTo:btocs@sph.com.sg"
              rel="noreferrer"
            >
              btocs@sph.com.sg
            </a>
            or call (65) 6388 3838
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>
              How do I terminate my subscription and delete my account?
            </strong>
          </p>

          <p>
            Please follow the instructions for termination as shared
            <a
              className="ml-1 text-verticals-btblue underline hover:text-black"
              target="_blank"
              href="https://consumerfaq.sph.com.sg/kb?id=kb_article_view&sys_kb_id=0d7131fa97b89e10eeee7301f053af16"
              rel="noreferrer"
            >
              here
            </a>
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>How do I update my profile information?</strong>
          </p>

          <p>
            The profile information can be updated on the
            <a
              className="ml-1 text-verticals-btblue underline hover:text-black"
              target="_blank"
              href="https://subscribe.sph.com.sg/your-account/"
              rel="noreferrer"
            >
              My Account Page
            </a>
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>Can I use my social media account to log in?</strong>
          </p>

          <p>
            Yes, you can log in with Google, Facebook, or Apple to create or
            access your SPH Media account. For more details please follow our
            <a
              className="ml-1 text-verticals-btblue underline hover:text-black"
              target="_blank"
              href="https://consumerfaq.sph.com.sg/kb?id=kb_article_view&sys_kb_id=b3402ac4978952509915b988c253afc5"
              rel="noreferrer"
            >
              consumer page
            </a>
          </p>
        </div>

        <div>
          <p>
            <strong>How do I log out of all devices?</strong>
          </p>

          <p>Yes, we support logout from all devices at once.</p>

          <ul className="mb-4 mt-8 list-decimal pl-16">
            <li>
              From
              <a
                className="mx-1 text-verticals-btblue underline hover:text-black"
                target="_blank"
                href="https://subscribe.sph.com.sg/your-account"
                rel="noreferrer"
              >
                &quot;my-account&quot;
              </a>
              page , go to the &quot;Devices&quot; section to log out from
              specific devices.
            </li>
            <li>
              Click <strong>&quot;Devices&quot;</strong> and the page will show
              you all the devices that you have logged in.
              <br />
              You can select the devices you want to log out from here.
            </li>
          </ul>
        </div>

        <div className="pl-16">
          <p>
            <strong>Can I log into more than one device?</strong>
          </p>

          <ul className="mb-4 list-disc pl-16">
            <li>
              It depends on your subscription plan. You can check your
              subscription plan
              <a
                className="ml-1 text-verticals-btblue underline hover:text-black"
                target="_blank"
                href="https://subscribe.sph.com.sg/your-subscriptions/"
                rel="noreferrer"
              >
                here
              </a>
            </li>
            <li>Log out after each session to avoid issues.</li>
          </ul>
        </div>
      </div>

      <div id="website-and-mobile-app-features">
        <BlockTitle
          text="4. Website/Mobile App Features"
          rootClassName="mb-4 text-verticals-btblue text-5xl font-medium lg:text-6xl"
        />

        <div>
          <p>
            <strong>How do I set up or manage notifications?</strong>
          </p>

          <ul className="mb-4 list-disc pl-16">
            <li className="mb-8">
              For iPhone and iPad (Turn On Notifications):
              <ul className="list-decimal pl-8">
                <li>
                  Open <strong>Settings</strong>.
                </li>

                <li>
                  Scroll down and tap <strong>Notifications</strong>.
                </li>

                <li>
                  Find <strong>Business Times</strong> and tap it.
                </li>

                <li>
                  Toggle <strong>Allow Notifications</strong> to
                  <strong>ON</strong> (green).
                </li>
              </ul>
            </li>

            <li className="mb-8 ml-16">
              For Android (Turn On Notifications):
              <ul className="list-decimal pl-8">
                <li>
                  Open the <strong>Business Times app</strong>.
                </li>

                <li>
                  Tap the <strong>three-dot menu</strong> (top-right).
                </li>

                <li>
                  Select <strong>Settings &gt Quiet Time</strong>.
                </li>

                <li>
                  Enable <strong>Push Notifications</strong>.
                </li>

                <li>
                  Choose <strong>Enable Sounds</strong> or
                  <strong>Enable Vibrate</strong>.
                </li>
              </ul>
            </li>

            <li className="mb-8">
              iPhone and iPad (To Turn Off Notifications):
              <ul className="list-decimal pl-8">
                <li>
                  Open <strong>Settings &gt Notifications</strong>.
                </li>

                <li>
                  Select <strong>Business Times</strong>.
                </li>

                <li>
                  Toggle <strong>Allow Notifications</strong> to
                  <strong>OFF</strong> (white).
                </li>
              </ul>
            </li>

            <li className="mb-8 ml-16">
              Android(To Turn Off Notifications):
              <ul className="list-decimal pl-8">
                <li>
                  Open the <strong>Business Times app</strong>.
                </li>

                <li>
                  Tap the
                  <strong>three-dot menu &gt Settings &gt Quiet Time</strong>.
                </li>

                <li>
                  Uncheck <strong>Enable Push</strong>,
                  <strong>Enable Sounds</strong>, and
                  <strong>Enable Vibrate</strong>.
                </li>
              </ul>
            </li>

            <li>
              For Website:
              <ul className="ml-8 list-none">
                <li>
                  Desktop/Laptop:
                  <ul className="list-decimal pl-8">
                    <li>
                      When you visit the website, look for a pop-up or banner
                      asking if you&apos;d like to receive notifications.
                    </li>

                    <li>Click Allow to enable notifications.</li>

                    <li>
                      If you don&apos;t see the pop-up:
                      <ul className="list-disc pl-8">
                        <li>
                          Go to your browser settings (usually in the top-right
                          corner).
                        </li>

                        <li>Look for Site Settings or Notifications.</li>

                        <li>Find Business Times and set it to Allow.</li>
                      </ul>
                    </li>
                  </ul>
                </li>

                <li>
                  To Disable Website Notifications:
                  <ul className="list-decimal pl-8">
                    <li>Go to your browser settings.</li>

                    <li>Navigate to Site Settings or Notifications.</li>

                    <li>Find Business Times and set it to Block.</li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </div>

        <div className="mb-8">
          <p>
            <strong>How do I search for specific content or features?</strong>
          </p>

          <p>
            On BT website, you can find the search icon on the top left
            corner\or App the search icon is located on the top right corner.
          </p>
        </div>

        <div>
          <p>
            <strong>
              What are the system requirements for using your app/website?
            </strong>
          </p>

          <p>
            To ensure the best experience with The Business Times app and
            website, please make sure your device meets the following
            requirements:
          </p>

          <ul className="mb-4 list-disc pl-16">
            <li>
              For the Mobile App:
              <ul className="mb-4 list-disc pl-16">
                <li>
                  iOS Devices (iPhone, iPad):
                  <ul className="mb-4 list-disc pl-16">
                    <li>Operating System: Latest 3 iOS versions</li>
                    <li>Storage: At least 105 MB of free space.</li>
                  </ul>
                </li>

                <li>
                  Android Devices:
                  <ul className="mb-4 list-disc pl-16">
                    <li>Operating System: Latest 3 OS version of Android.</li>
                    <li>Storage: At least 105 MB of free space.</li>
                  </ul>
                </li>
              </ul>
            </li>

            <li>
              For the Website:
              <ul className="mb-4 list-disc pl-16">
                <li>
                  Supported Browsers:
                  <ul className="mb-4 list-disc pl-16">
                    <li>
                      Latest versions of Google Chrome, Mozilla Firefox, Safari,
                      or Microsoft Edge.
                    </li>
                    <li>
                      Ensure your browser is updated for the best performance.
                    </li>
                  </ul>
                </li>

                <li>
                  Device Compatibility:
                  <ul className="mb-4 list-disc pl-16">
                    <li>
                      The website is compatible with desktops, laptops, tablets,
                      and smartphones.
                    </li>
                    <li>
                      For the best experience, use a device with a screen
                      resolution of 1024x768 or higher.
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </div>

        <div>
          <p>
            <strong>
              How do I find back issues of The Business Times on the app?
            </strong>
          </p>

          <ul className="mb-4 list-disc pl-16">
            <li>
              Tap the calendar icon in E-paper tab on the app to access
              archives. Subscribers can view past 15 days of epaper.
            </li>
          </ul>
        </div>

        <div>
          <p>
            <strong>What if the app crashes or doesn&apos;t update?</strong>
          </p>

          <ul className="mb-4 list-disc pl-16">
            <li>
              Force-quit the app and reopen it. If the issue persists, restart
              your device or reinstall the app.
            </li>
            <li>
              Email customer service at btocs@sph.com.sg or call (65) 6388 3838
              for further assistance.
            </li>
          </ul>
        </div>

        <div>
          <p>
            <strong>
              How do I manage my subscription and auto-renewal on iOS?
            </strong>
          </p>

          <ul className="mb-4 list-disc pl-16">
            <li>
              Go to the App Store &gt Account &gt Subscriptions &gt Manage App
              Subscriptions.
            </li>
          </ul>
        </div>
      </div>

      <div id="general-inquiries">
        <BlockTitle
          text="5. General Inquiries"
          rootClassName="mb-4 text-verticals-btblue text-5xl font-medium lg:text-6xl"
        />

        <div className="mb-8">
          <p>
            <strong>How do I contact customer support?</strong>
          </p>

          <p>
            Our customer service team can be contacted via email at
            <a
              className="mx-1 text-verticals-btblue underline hover:text-black"
              target="_blank"
              href="mailTo:btocs@sph.com.sg"
              rel="noreferrer"
            >
              btocs@sph.com.sg
            </a>
            or call (65) 6388 3838.
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>
              Where can I find your terms of service and privacy policy?
            </strong>
          </p>

          <p>
            Visit our
            <a
              className="mx-1 text-verticals-btblue underline hover:text-black"
              target="_blank"
              href="https://www.sph.com.sg/tnc/privacy?ref=footer&_gl=1*1c14kmb*_gcl_au*MTA5NzIyMjc2Ny4xNzM3NjA1Mzk2"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
            page.
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>
              Where can I find your community forum or social media pages?
            </strong>
          </p>

          <p>
            BT can be found on almost all common social media Twitter, Facebook,
            LinkedIn, Instagram, WhatsApp, and Telegram.
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>What is BT RSS Feed?</strong>
          </p>

          <p>
            You may choose from a full list of our
            <a
              className="ml-1 text-verticals-btblue underline hover:text-black"
              target="_blank"
              href="http://businesstimes.com.sg/rss-feeds"
              rel="noreferrer"
            >
              RSS feeds here
            </a>
            .
          </p>
        </div>

        <p className="underline">Feature : Gift an Article</p>

        <div className="mb-8">
          <p>
            <strong>How many articles can I gift?</strong>
          </p>

          <p>BT subscribers can gift up to 5 premium articles each month.</p>
        </div>

        <div className="mb-8">
          <p>
            <strong>
              How do I know if I&apos;ve reached my gifted article limit?
            </strong>
          </p>

          <p>
            When gifting an article, the pop-up window will show the number of
            articles you are left to gift. Eg &quot;You currently have 4
            articles left to gift for this month.&quot;
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>
              Can I gift an article marked as &quot;Subscriber Only&quot;?
            </strong>
          </p>

          <p>
            Yes, you can gift articles marked as &quot;Subscriber Only.&quot;
            However, other articles that are not marked as
            &quot;Subscriber&quot; can be read without a subscription.
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>
              I&apos;ve received a link to a gift article. What does this mean?
            </strong>
          </p>

          <p>
            This means you are gifted to read the premium content from business
            times for free. The shared link will be valid for 30 days.
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>How long do I have free access to gifted article?</strong>
          </p>

          <p>The link to the gifted story will be valid for 30 days.</p>
        </div>

        <p className="underline">Feature : Bookmark</p>

        <div className="mb-8">
          <p>
            <strong>Where can bookmark features be found?</strong>
          </p>

          <p>
            The bookmark feature on the BT app allows users to save or mark
            specific content for easy access later.
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>How do I access my bookmarked items?</strong>
          </p>

          <p>
            The bookmarked content can be found on top of the section tab on BT
            App.
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>How many max articles can be bookmarked</strong>
          </p>

          <p>A maximum of 200 articles can be bookmarked.</p>
        </div>

        <div className="mb-8">
          <p>
            <strong>What if I have reached the bookmark limit </strong>
          </p>

          <p>
            If you reach the bookmarked limit, you can remove the older
            bookmarks and free up the quota.
          </p>
        </div>

        <p className="underline">Feature : Newsletter</p>

        <div className="mb-8">
          <p>
            <strong>What are the newsletters offered by Business Times</strong>
          </p>

          <p>
            Business times currently offers 9 newsletters daily, weekly and
            premium. These newsletters can be subscribed
            <a
              className="ml-1 text-verticals-btblue underline hover:text-black"
              target="_blank"
              href="https://www.businesstimes.com.sg/newsletter/sign-up?ref=hamburger-menu"
              rel="noreferrer"
            >
              here
            </a>
            .
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>
              Are there limits to how many newsletters I can subscribe to?
            </strong>
          </p>

          <p>
            The subscription to newsletters is not limited. You can subscribe to
            all the newsletters.
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>Can I share newsletters with others?</strong>
          </p>

          <p>Yes, you can share newsletters with others.</p>
        </div>

        <div className="mb-8">
          <p>
            <strong>How can I unsubscribe from a newsletter?</strong>
          </p>

          <p>
            To unsubscribe from the newsletter, you can click on the unsubscribe
            link present in the bottom of the newsletter.
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>
              How do I update my email address for newsletter delivery?
            </strong>
          </p>

          <p>
            You can unsubscribe using your email address and subscribe with a
            new email address.
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>
              Where can I find past newsletters I&apos;ve received?
            </strong>
          </p>

          <p>
            To access the old version of the newsletter, please click on the
            name of the newsletter.
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>
              I&apos;ve signed up for the newsletter. Why am I not receiving
              them?
            </strong>
          </p>

          <p>
            Please check your junk folder in your email inbox. If you find that
            you still have not received the newsletter, please email
            <a
              className="mx-1 text-verticals-btblue underline hover:text-black"
              target="_blank"
              href="mailTo:btocs@sph.com.sg"
              rel="noreferrer"
            >
              btocs@sph.com.sg
            </a>
            or call our customer service team at
            <strong>(65) 6388 3838</strong>.
          </p>
        </div>

        <p className="underline">Feature : ePaper</p>

        <div className="mb-8">
          <p>
            <strong>How can i access epaper?</strong>
          </p>

          <p>
            On website epaper can be accessed by clicking on &apos;Epaper&apos;
            on top right corner
            <br />
            On App you can access Epaper in the tab below on the landing page.
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>
              Does the ePaper allow me to save content offline for later
              reading?
            </strong>
          </p>

          <p>
            To access the old version of the newsletter, please click on the
            name of the newsletter.Yes, you can download the epaper you wish to
            read later.
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>Can I zoom in on articles in the ePaper?</strong>
          </p>

          <p>The epaper page can be zoomed for reading</p>
        </div>

        <div className="mb-8">
          <p>
            <strong>What is crop function in epaper</strong>
          </p>

          <p>
            Crop function allows you to select a portion of the article. This
            can be saved for later reading or memory snapshot.
          </p>
        </div>
      </div>

      <div id="technical-issues">
        <BlockTitle
          text="6. Technical Issues"
          rootClassName="mb-4 text-verticals-btblue text-5xl font-medium lg:text-6xl"
        />

        <div>
          <p>
            <strong>The app/website is not loading. What should I do?</strong>
          </p>

          <p className="pl-8">
            If <strong>The Business Times</strong> app or website is not
            loading, try these steps to resolve the issue:
          </p>

          <ul className="mb-4 list-decimal pl-16">
            <li>
              Check Your Internet Connection:
              <ul className="list-disc pl-16">
                <li>
                  Ensure your device is connected to a stable Wi-Fi or mobile
                  data network.
                </li>

                <li>
                  Try opening another website or app to confirm your internet is
                  working.
                </li>
              </ul>
            </li>

            <li>
              Restart the App or Browser:
              <ul className="list-disc pl-16">
                <li>Close the app or browser completely and reopen it.</li>

                <li>
                  For mobile apps, swipe up (iOS) or use the app switcher
                  (Android) to force-close the app, then relaunch it.
                </li>
              </ul>
            </li>

            <li>
              Clear Cache (Website Only):
              <ul className="list-disc pl-16">
                <li>
                  If the website isn&apos;t loading, clear your browser&apos;s
                  cache:
                  <ul className="list-disc pl-16">
                    <li>
                      <strong>Chrome</strong>: Go to Settings &gt Privacy &gt
                      Clear Browsing Data.
                    </li>

                    <li>
                      <strong>Safari</strong>: Go to Settings &gt Safari &gt
                      Clear History and Website Data.
                    </li>

                    <li>
                      <strong>Firefox</strong>: Go to Settings &gt Privacy &gt
                      Clear Private Data.
                    </li>
                  </ul>
                </li>
              </ul>
            </li>

            <li>
              Update the App or Browser:
              <ul className="list-disc pl-16">
                <li>
                  Ensure you&apos;re using the latest version of
                  <strong>The Business Times</strong> app or your browser.
                </li>

                <li>
                  Visit the <strong>App Store</strong> (iOS) or
                  <strong>Google Play Store</strong> (Android) to check for app
                  updates.
                </li>

                <li>Update your browser through its settings or app store.</li>
              </ul>
            </li>

            <li>
              Restart Your Device:
              <ul className="list-disc pl-16">
                <li>
                  Turn your device off and on again to refresh its system.
                </li>
              </ul>
            </li>

            <li>
              Check for Service Issues:
              <ul className="list-disc pl-16">
                <li>
                  Visit <strong>The Business Times</strong> social media pages
                  or contact customer support to check if there&apos;s a known
                  outage or maintenance.
                </li>
              </ul>
            </li>

            <li>
              Reinstall the App (Mobile Only):
              <ul className="list-disc pl-16">
                <li>
                  Delete the app from your device and reinstall it from the
                  <strong>App Store</strong> (iOS) or
                  <strong>Google Play Store</strong> (Android).
                </li>
              </ul>
            </li>

            <li>
              Contact Support:
              <ul className="list-disc pl-16">
                <li>
                  If the issue persists, contact our customer service team for
                  assistance:
                  <ul className="list-disc pl-16">
                    <li>
                      Email:
                      <a
                        className="ml-1 text-verticals-btblue underline hover:text-black"
                        target="_blank"
                        href="mailTo:btocs@sph.com.sg"
                        rel="noreferrer"
                      >
                        btocs@sph.com.sg
                      </a>
                    </li>

                    <li>Call :(65) 6388 3838</li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </div>

        <div>
          <p>
            <strong>How do I clear my cache and cookies?</strong>
          </p>

          <ul className="mb-4 pl-16">
            <li className="mb-8">
              Google Chrome (Desktop)
              <ul className="list-decimal pl-16">
                <li>Open Chrome.</li>

                <li>Click on the three-dot menu (top-right corner).</li>

                <li>
                  Select &quot;More tools&quot; and then &quot;Clear browsing
                  data...&quot;.
                </li>

                <li>
                  In the pop-up window, set the Time range to All time to clear
                  everything.
                </li>

                <li>
                  Check &quot;Cookies and other site data&quot; and &quot;Cached
                  images and files&quot;.
                </li>

                <li>Click &quot;Clear data&quot;.</li>
              </ul>
            </li>

            <li className="mb-8">
              Mozilla Firefox (Desktop)
              <ul className="list-decimal pl-16">
                <li>Open Firefox.</li>

                <li>Click on the three-line menu (top-right corner).</li>

                <li>Select &quot;Settings&quot;.</li>

                <li>Go to the Privacy & Security panel.</li>

                <li>Scroll down to the Cookies and Site Data section.</li>

                <li>Click on &quot;Clear Data...&quot;.</li>

                <li>
                  Check both Cookies and Site Data and Cached Web Content.
                </li>

                <li>Click &quot;Clear&quot;.</li>
              </ul>
            </li>

            <li>
              Safari (Desktop)
              <ul className="list-decimal pl-16">
                <li>Open Safari.</li>

                <li>
                  Click on Safari in the top menu bar and select Preferences.
                </li>

                <li>Go to the Privacy tab.</li>

                <li>Click Manage Website Data....</li>

                <li>Select Remove All to clear cookies and cache.</li>
              </ul>
            </li>
          </ul>
        </div>

        <div>
          <p>
            <strong>How do I update the app to the latest version?</strong>
          </p>

          <ul className="mb-4 pl-16">
            <li className="mb-8">
              Android
              <ul className="list-decimal pl-16">
                <li>Open the Google Play Store.</li>

                <li>Tap Menu (three lines) &gt; My apps &amp; games.</li>

                <li>Tap Update All or Update next to the app.</li>
              </ul>
            </li>

            <li>
              iPhone
              <ul className="list-decimal pl-16">
                <li>Open the App Store.</li>

                <li>Tap your profile icon &gt; Available Updates.</li>

                <li>Tap Update All or Update next to the app.</li>
              </ul>
            </li>
          </ul>
        </div>

        <div>
          <p>
            <strong>How do I enable notifications?</strong>
          </p>

          <ul className="mb-4 pl-16">
            <li className="mb-8">
              Android
              <ul className="list-decimal pl-16">
                <li>
                  Open <strong>Settings</strong>.
                </li>

                <li>
                  Tap
                  <strong>Apps &amp; notifications &gt; Notifications</strong>.
                </li>

                <li>
                  Toggle <strong>Allow notifications</strong> to enable them.
                </li>
              </ul>
            </li>

            <li>
              iPhone
              <ul className="list-decimal pl-16">
                <li>
                  Open <strong>Settings</strong>.
                </li>

                <li>Tap Notifications.</li>

                <li>
                  Toggle <strong>Allow Notifications</strong> for the desired
                  app.
                </li>
              </ul>
            </li>
          </ul>
        </div>

        <div>
          <p>
            <strong>Is the app/website compatible with my device?</strong>
          </p>

          <ul className="mb-4 pl-16">
            <li className="mb-8">
              To check app compatibility:
              <ul className="list-disc pl-16">
                <li>
                  Android: Go to Google Play Store, search for the app, and
                  check the &quot;Requires Android&quot; section under app
                  details.
                </li>

                <li>
                  iPhone: Go to App Store, search for the app, and check the
                  &quot;Compatibility&quot; section for your iOS version.
                </li>
              </ul>
            </li>

            <li>
              To check website compatibility:
              <ul className="list-disc pl-16">
                <li>Ensure your device&apos;s browser is updated.</li>

                <li>
                  Visit the website; if it loads properly, it&apos;s compatible.
                  If there are issues, check the browser requirements or contact
                  the website&apos;s support.
                </li>
              </ul>
            </li>
          </ul>
        </div>

        <div>
          <p>
            <strong>How do I enable or disable auto-updates?</strong>
          </p>

          <ul className="mb-4 pl-16">
            <li className="mb-8">
              Android
              <ul className="list-decimal pl-16">
                <li>
                  Google Support Website:
                  <br />
                  Visit the Google Play Help Center for official instructions on
                  managing auto-updates.
                </li>

                <li>
                  Device Settings:
                  <br />
                  Go to Settings &gt Apps or Google Play Store &gt Settings &gt
                  Auto-update apps to enable or disable auto-updates.
                </li>
              </ul>
            </li>

            <li>
              Mozilla Firefox (Desktop)
              <ul className="list-decimal pl-16">
                <li>
                  Apple Support Website:
                  <br />
                  Visit the Apple Support page for the latest information on
                  managing app updates/
                </li>

                <li>
                  Device Settings:
                  <br />
                  Go to Settings &gt App Store &gt Toggle App Updates to enable
                  or disable auto-updates.
                </li>
              </ul>
            </li>
          </ul>
        </div>

        <div>
          <p>
            <strong>The app keeps crashing. How do I fix it?</strong>
          </p>

          <ul className="mb-4 pl-16">
            <li className="mb-8">
              For Android:
              <ul className="list-decimal pl-16">
                <li>Restart your phone.</li>
                <li>
                  Update the app: Open Google Play Store &gt; search for the app
                  &gt; tap Update.
                </li>

                <li>
                  Clear cache and data: Go to Settings &gt; Apps &gt; select the
                  app &gt; tap Storage &gt; Clear Cache and Clear Data.
                </li>

                <li>
                  Reinstall the app: Uninstall the app and then reinstall it
                  from the Google Play Store.
                </li>
              </ul>
            </li>

            <li>
              For iPhone:
              <ul className="list-decimal pl-16">
                <li>Restart your phone.</li>
                <li>
                  Update the app: Open App Store &gt; search for the app &gt;
                  tap Update.
                </li>
                <li>
                  Clear app data: Go to Settings &gt; General &gt; iPhone
                  Storage &gt; select the app &gt; tap Delete App. Then
                  reinstall it from the App Store.
                </li>
              </ul>
            </li>
          </ul>
        </div>

        <div className="mb-8">
          <p>
            <strong>How do I report a bug or technical issue?</strong>
          </p>
          <p>
            For technical issue on the website, please email to us at or call us
            at (65) 6388 3838
          </p>

          <p>
            In Mobile App, you can go to profile → General Feedback and write to
            us.
          </p>
        </div>

        <div className="mb-8">
          <p>
            <strong>Why can&apos;t I access certain features?</strong>
          </p>
          <p>
            Certain features like Gift of Article, Epaper and myBT are available
            for subscribers only. To enjoy these features, please subscribe to
            BT.
          </p>
        </div>

        <div>
          <p>
            <strong>Why am I experiencing slow performance?</strong>
          </p>

          <p>
            To improve the slow performance on your device, please follow these
            steps:
          </p>

          <ul className="mb-4 list-decimal pl-16">
            <li>
              <strong>Clear Cache & Cookies:</strong> Delete old data in the
              browser or app settings to improve speed.
            </li>

            <li>
              <strong>Update Software:</strong> Ensure the app, browser, or
              website is using the latest version to fix bugs and improve
              performance.
            </li>

            <li>
              <strong>Close Unnecessary Tabs/Apps:</strong> Limit the number of
              open tabs or apps to free up memory and processing power.
            </li>

            <li>
              <strong>Check Your Internet Connection:</strong> Ensure you have a
              stable and fast internet connection.
            </li>

            <li>
              <strong>Test on Another Device:</strong> Try using the app or
              website on a different device to rule out device-specific issues.
            </li>

            <li>
              <strong>Disable Unnecessary Extensions:</strong> Turn off browser
              add-ons or app features that aren&apos;t needed.
            </li>

            <li>
              <strong>Restart Your Device:</strong> Restarting clears memory and
              can fix slow performance.
            </li>

            <li>
              <strong>Contact Support:</strong> If problems persist, reach out
              to customer support (
              <a
                className="mx-1 underline"
                target="_blank"
                href="mailTo:btocs@sph.com.sg"
                rel="noreferrer"
              >
                btocs@sph.com.sg
              </a>
              or call us at (65) 6388 3838) for further help.
            </li>
          </ul>
        </div>
      </div>
    </Container>
  );
};

export default HelpLayout;
